import styled from 'styled-components';
import { useIsMobile } from '@app/contexts/viewport';

// Custom media query - not using theme
const mobileMedia = '@media (width <= 1024px)';

/**
 * PlannerSkeleton - Full-page skeleton shown while Core is initializing
 *
 * This component provides a loading skeleton that mimics the planner layout:
 * - Header skeleton
 * - Canvas area with loading spinner
 * - Sidebar with product skeleton items
 */

const Root = styled.div<{ $isReady?: boolean; $isMobile?: boolean }>`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 9999;
  direction: ltr;
  background-color: ${props => (props.$isMobile ? '#f5f5f5' : '#fff')};
  opacity: ${props => (props.$isReady === false ? 0 : 1)};
  pointer-events: ${props => (props.$isReady === false ? 'none' : 'auto')};
  visibility: ${props => (props.$isReady === false ? 'hidden' : 'visible')};
  animation: ${props => (props.$isReady === false ? 'fade-out 0.5s ease-in-out forwards' : 'none')};
  transition: visibility 0s linear ${props => (props.$isReady === false ? '0.5s' : '0s')};
  contain: layout style;

  @keyframes fade-out {
    0% {
      opacity: 1;
      filter: blur(0);
    }

    100% {
      opacity: 0;
      filter: blur(4px);
    }
  }
`;

const SkeletonPulse = styled.div`
  position: relative;
  overflow: hidden;
  background-color: #d1d5db;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%) skewX(-15deg);
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgb(255 255 255 / 10%) 25%,
      rgb(255 255 255 / 60%) 50%,
      rgb(255 255 255 / 10%) 75%,
      transparent 100%
    );
    animation: wave 2.2s cubic-bezier(0.4, 0, 0.2, 1) 0.5s infinite;
  }

  @keyframes wave {
    0% {
      transform: translateX(-100%) skewX(-15deg);
    }

    60% {
      transform: translateX(100%) skewX(-15deg);
    }

    100% {
      transform: translateX(100%) skewX(-15deg);
    }
  }
`;

// Floating header buttons (top-left of canvas)
const FloatingHeaderLeft = styled.div`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  display: flex;
  gap: 0.75rem;
  z-index: 10;
  justify-content: center;
  align-items: center;

  ${mobileMedia} {
    display: none;
  }
`;

// Floating header buttons (top-right of canvas)
const FloatingHeaderRight = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  z-index: 10;

  ${mobileMedia} {
    display: none;
  }
`;

const MenuButtonSkeleton = styled(SkeletonPulse)`
  width: 56px;
  height: 56px;
  background-color: #e5e7eb;
  border-radius: 50%;
`;

const SaveButtonSkeleton = styled(SkeletonPulse)`
  width: 105px;
  height: 40px;
  background-color: #e5e7eb;
  border-radius: 64px;
`;

const PriceSkeletonFloat = styled(SkeletonPulse)`
  height: 28px;
  width: 80px;
  background-color: #e5e7eb;
  border-radius: 4px;
`;

const SummaryButtonSkeleton = styled(SkeletonPulse)`
  height: 56px;
  width: 170px;
  background-color: #e5e7eb;
  border-radius: 64px;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;

  ${mobileMedia} {
    flex-direction: column;
    height: 100%;
  }
`;

const CanvasArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  width: calc(100% - 30rem);
  position: relative;

  ${mobileMedia} {
    display: none;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: transparent;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
`;

const MobileMenuButton = styled(SkeletonPulse)`
  width: 40px;
  height: 40px;
  background-color: #e5e7eb;
  border-radius: 50%;
  flex-shrink: 0;
`;

const MobilePriceSkeleton = styled(SkeletonPulse)`
  height: 28px;
  width: 80px;
  background-color: #e5e7eb;
  border-radius: 4px;
`;

const MobileActionButton = styled(SkeletonPulse)`
  width: 40px;
  height: 40px;
  background-color: #e5e7eb;
  border-radius: 50%;
  flex-shrink: 0;
`;

const MobileCanvasButtonsLeft = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 1rem;
  display: flex;
  gap: 1rem;
`;

const MobileCanvasButton = styled(SkeletonPulse)`
  width: 40px;
  height: 40px;
  background-color: #e5e7eb;
  border-radius: 50%;
`;

const MobileBottomPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background-color: #fff;
  padding: 0.75rem;
  gap: 0.85rem;
  box-sizing: border-box;
  height: 140px;
`;

const MobileTitleSkeleton = styled(SkeletonPulse)`
  height: 24px;
  width: 160px;
  background-color: #e5e7eb;
  border-radius: 4px;
`;

const MobilePillsContainer = styled.div`
  display: flex;
  gap: 1rem;
  overflow: hidden;
`;

const MobilePillSkeleton = styled(SkeletonPulse)`
  height: 56px;
  width: 140px;
  background-color: #e5e7eb;
  border-radius: 64px;
  flex-shrink: 0;
`;

const MobilePillChevron = styled(SkeletonPulse)`
  width: 56px;
  height: 56px;
  background-color: #e5e7eb;
  border-radius: 50%;
  flex-shrink: 0;
`;

const CanvasToolbar = styled.div`
  position: absolute;
  left: 1.2rem;
  bottom: 1.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CanvasButton = styled(SkeletonPulse)`
  width: 40px;
  height: 40px;
  background-color: #e5e7eb;
  border-radius: 50%;
  flex-shrink: 0;
`;

const CanvasNavigation = styled.div`
  position: absolute;
  bottom: 1.7rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const CustomizeButton = styled(SkeletonPulse)`
  height: 40px;
  width: 160px;
  border-radius: 24px;
`;

const Sidebar = styled.aside`
  width: 30rem;
  background-color: #fff;
  border-left: 1px solid #dfdfdf;
  flex-shrink: 0;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;

  ${mobileMedia} {
    display: none;
  }
`;

const MenuWrapper = styled.div`
  display: flex;
  height: 100%;
  position: relative;
  flex-direction: column;
  overflow-y: auto;
`;

const SidebarTitle = styled.div`
  margin: 1.5rem 2rem 0.5rem 2.8rem;
  min-height: 3.5rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;

  ${mobileMedia} {
    padding: 1.5rem 1rem;
  }
`;

const TitleSkeleton = styled(SkeletonPulse)`
  height: 36px;
  width: 220px;
  background-color: #e5e7eb;
  border-radius: 4px;

  ${mobileMedia} {
    height: 32px;
    width: 200px;
  }
`;

const SkeletonBox = styled(SkeletonPulse)`
  background-color: #e5e7eb;
  border-radius: 4px;
`;

const SkeletonContainer = styled.div`
  padding: 0.3rem 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0;

  ${mobileMedia} {
    padding: 0.75rem 1.3rem 2rem;
    gap: 0;
  }
`;

const SkeletonItem = styled.div`
  display: flex;
  gap: 1rem 0.5rem;
  align-items: center;
  padding: 1.5rem;
  border-top: 1px solid #dfdfdf;
  border-bottom: 1px solid #dfdfdf;

  & + & {
    border-top: none;
  }

  &:first-child {
    border-top: none;
  }

  &:last-child {
    border-bottom: none;
  }

  ${mobileMedia} {
    padding: 1rem 1.3rem;
    gap: 1rem;
  }
`;

const SkeletonIcon = styled(SkeletonBox)`
  width: 28px;
  height: 28px;
  flex-shrink: 0;

  ${mobileMedia} {
    width: 32px;
    height: 32px;
  }
`;

const SkeletonContent = styled.div`
  flex: 1;
  margin: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${mobileMedia} {
    gap: 0;
  }
`;

const SkeletonTitle = styled(SkeletonBox)`
  height: 20px;
  width: 140px;

  ${mobileMedia} {
    height: 20px;
    width: 140px;
  }
`;

const SkeletonChevron = styled(SkeletonBox)`
  width: 10px;
  height: 16px;
  flex-shrink: 0;
  margin-left: auto;

  ${mobileMedia} {
    width: 12px;
    height: 18px;
  }
`;

type Props = {
  isReady?: boolean;
};

export const PlannerSkeleton = ({ isReady }: Props) => {
  const isMobileDevice = useIsMobile();
  // Mobile layout - using inline styles for critical layout to prevent CSS hydration shifts
  if (isMobileDevice) {
    return (
      <Root $isReady={isReady} $isMobile>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
          {/* Mobile Header - absolutely positioned over canvas */}
          <MobileHeader>
            <MobileMenuButton />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <MobilePriceSkeleton />
              <MobileActionButton />
            </div>
          </MobileHeader>

          {/* Mobile Canvas Area */}
          <div
            style={{
              flex: '1 1 auto',
              backgroundColor: '#f5f5f5',
              position: 'relative',
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <MobileCanvasButtonsLeft>
              <MobileCanvasButton />
              <MobileCanvasButton />
            </MobileCanvasButtonsLeft>
          </div>

          {/* Mobile Bottom Panel */}
          <MobileBottomPanel>
            <MobileTitleSkeleton />
            <MobilePillsContainer>
              <MobilePillSkeleton />
              <MobilePillSkeleton />
              <MobilePillChevron />
            </MobilePillsContainer>
          </MobileBottomPanel>
        </div>
      </Root>
    );
  }

  // Desktop layout
  return (
    <Root $isReady={isReady}>
      <Main>
        <CanvasArea>
          {/* Floating header buttons - top left */}
          <FloatingHeaderLeft>
            <MenuButtonSkeleton />
            <SaveButtonSkeleton />
          </FloatingHeaderLeft>
          {/* Floating header buttons - top right */}
          <FloatingHeaderRight>
            <PriceSkeletonFloat />
            <SummaryButtonSkeleton />
          </FloatingHeaderRight>
          <CanvasToolbar>
            <CanvasButton />
            <CanvasButton />
            <CanvasButton />
            <CanvasButton />
          </CanvasToolbar>
          <CanvasNavigation>
            <CustomizeButton />
          </CanvasNavigation>
        </CanvasArea>
        <Sidebar>
          <MenuWrapper>
            <SidebarTitle>
              <TitleSkeleton />
            </SidebarTitle>
            <SkeletonContainer>
              {[...Array(7)].map((_, i) => (
                <SkeletonItem key={i}>
                  <SkeletonIcon />
                  <SkeletonContent>
                    <SkeletonTitle />
                  </SkeletonContent>
                  <SkeletonChevron />
                </SkeletonItem>
              ))}
            </SkeletonContainer>
          </MenuWrapper>
        </Sidebar>
      </Main>
    </Root>
  );
};
